import React, { useMemo, useRef } from 'react';
import clsx from 'clsx';
import PlaceModalBookingWidget, {
  getDefaultPlaceModalBookingParams,
  type PlaceModalBookingWidgetRef,
  type PlaceModalSearchParams,
} from './PlaceModalBookingWidget';
import PlaceModalHero from './PlaceModalHero';
import { PlaceModalAbout, PlaceModalInfoRows } from './PlaceModalDetails';
import PlaceModalSocialProof, { PlaceModalGoogleRating, PlaceModalRecommenders } from './PlaceModalSocialProof';
import PlaceModalCommunityTips from './PlaceModalCommunityTips';
import PlaceModalFeaturedTrips, { type FeaturedTripCardData } from './PlaceModalFeaturedTrips';
import PlaceStayBookingOptions from '@/components/Stay22Booking/PlaceStayBookingOptions';
import GygActivityWidget from '@/components/ExperiencesBooking/GygActivityWidget';
import { useTrackHotelWidgetShown } from '@/hooks/useTrackHotelWidgetShown';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import type { HotelWidgetShownAnalyticsProps } from '@/lib/analyticsAttribution';
import type { Stay22AllezProvider } from '@/lib/booking/stay22AllezProviders';
import { getPlaceCategoryDisplay } from '@/utils/placeCategoryDisplay';
import { getCityDisplayText, formatLocationLabelCityCountry } from '@/utils/cityUtils';
import {
  extractGoogleOpeningHoursDisplay,
  getGoogleUserRatingCountUnified,
  pickPrimaryGoogleMapsUrl,
  stripGooglePlacesResourcePrefix,
} from '@/utils/googlePlaceOverview';
import type Post from '@/models/post';
import type { Location } from '@/models/locationModel';
import type { RecommenderAvatar } from '@/utils/locationDataExtractor';

/** Mobile "Mobile Heading 2" for section titles that are Manrope on desktop. */
const MOBILE_SECTION_HEADING = 'max-md:font-lora max-md:text-lg max-md:leading-6 max-md:tracking-[0.5px]';
const BAR_BUTTON =
  'inline-flex h-9 min-h-9 flex-1 items-center justify-center rounded-[10px] px-4 font-manrope text-[13px] leading-5 tracking-[0.5px] text-white no-underline transition-colors max-md:h-12 max-md:min-h-12 max-md:w-full max-md:flex-none';
const BAR_PRIMARY = clsx(BAR_BUTTON, 'bg-[#FF5212] hover:bg-[#e64910] disabled:opacity-60');

const ORANGE_BUTTON =
  'inline-flex items-center justify-center rounded-[14px] border border-solid border-[#E1E1E7] bg-[#FF5212] px-4 py-2 font-manrope text-sm font-medium leading-5 tracking-[0.5px] text-white transition-colors hover:bg-[#e64910] disabled:cursor-not-allowed disabled:opacity-60';

/** Google rating extracted safely from google_places_data (handles $numberDouble wrapper). */
function getGoogleRating(locationData: Partial<Location> | undefined): number | null {
  const raw = locationData?.google_places_data?.rating ?? locationData?.google_rating;
  if (raw == null) return null;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'object' && '$numberDouble' in raw) {
    const n = parseFloat((raw as { $numberDouble: string }).$numberDouble);
    return isNaN(n) ? null : n;
  }
  return null;
}

/** The backend's plain `city_name`, else the legacy `city` field. */
function getPlaceCity(cityName: string | undefined, locationData: Partial<Location> | undefined): string {
  const rawCity = locationData?.city;
  return (
    cityName?.trim() ||
    (typeof rawCity === 'string' ? rawCity.trim() : getCityDisplayText(rawCity) ?? '')
  );
}

/**
 * "Cartagena, Colombia" for the hero meta row: the place's city (see getPlaceCity), else the
 * address' first and last parts.
 */
function getPlaceCityLabel(
  cityName: string | undefined,
  countryName: string | undefined,
  locationData: Partial<Location> | undefined,
  address: string,
): string {
  const city = getPlaceCity(cityName, locationData);
  const country = (countryName ?? locationData?.country)?.trim();
  if (city) return country && !city.includes(country) ? `${city}, ${country}` : city;
  return address && address !== 'Address not available' ? formatLocationLabelCityCountry(address) : '';
}

function buildDirectionsUrl(
  locationData: Partial<Location> | undefined,
  name: string,
  address: string,
): string | null {
  const destination = [name, address].filter(Boolean).join(', ');
  if (!destination) return null;
  const params = new URLSearchParams({ api: '1', destination });
  const placeId = locationData?.google_place_id ?? locationData?.google_places_data?.place_id;
  if (placeId) params.set('destination_place_id', stripGooglePlacesResourcePrefix(placeId));
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

export interface PlaceModalBodyProps {
  locationData: Partial<Location> | undefined;
  name: string;
  address: string;
  description?: string;
  phone?: string;
  website?: string;
  /** Plain city / country from GET /api/v2/location/:id. */
  cityName?: string;
  country?: string;
  /** Admin-set restaurant links (GET /api/v2/location/:id). */
  menuUrl?: string;
  reservationUrl?: string;
  openTableRid?: string;
  photoUrls: string[];
  onPhotosClick: () => void;
  explicitRecommendations: number;
  recommendationAvatars: RecommenderAvatar[];
  /** Mongo location id for Community Tips. */
  tipsLocationId: string | undefined;
  tipsRefreshSignal: number;
  onReadMoreTips: () => void;
  onAddTip: () => void;
  isLoggedIn: boolean;
  onSignInClick: () => void;
  onAddToTripClick: () => void;
  // Featured in trips & lists
  totalPostsCount: number;
  associatedPosts: Post[];
  postsLoading: boolean;
  postsError: string | null;
  /** Logged out and the posts endpoint wants a token: show the sign-in prompt. */
  postsNeedSignIn: boolean;
  isCreatingTrip: boolean;
  isEnriching: boolean;
  onCreateTrip: () => void;
  onRetryPosts: () => void;
  mapPostToCard: (post: Post) => FeaturedTripCardData;
  // Stays
  isStayCategory: boolean;
  canBook: boolean;
  isBookLoading: boolean;
  onBookNow: (params?: PlaceModalSearchParams) => void;
  /** Inline GT Exclusive row (and OTA rows) instead of a single Book button. */
  showInlineStayBookingOptions?: boolean;
  /** Booking.com / Expedia / Hotels.com rows; they open via Stay22. */
  showStay22Vendors?: boolean;
  isNuiteeBookLoading?: boolean;
  isStay22BookLoading?: boolean;
  onPlaceStayBookNuitee?: (params: PlaceModalSearchParams) => void;
  onPlaceStayBookStay22Vendor?: (provider: Stay22AllezProvider, params: PlaceModalSearchParams) => void;
  onGtExclusiveSignInClick?: () => void;
  /** Attribution context for Hotel Widget Shown when the stay booking card is visible. */
  hotelWidgetImpression?: Omit<HotelWidgetShownAnalyticsProps, 'surface' | 'cta_type'>;
  // Experiences
  canShowExperiences?: boolean;
  experiencesSearchQuery?: string | null;
  experiencesPostId?: string;
  experiencesPlaceId?: string;
  experiencesUserId?: string;
  experiencesGooglePlaceId?: string;
  onBrowseExperiences?: () => void;
  isExperiencesLoading?: boolean;
}

/**
 * Figma "Place Page" (side panel) below the toolbar. One layout with three variants:
 * stays get a booking card beside the details, experiences get "Tours & experiences",
 * everything else (restaurants, landmarks without GYG) shows details only.
 *
 * Mobile (Figma "· Mobile" frames) stacks the same sections 32px apart. The Google rating sits
 * beside the phone row, the recommenders head Community Tips (or precede Tours & experiences,
 * with a link down to the tips), and the bottom bar stacks the variant CTA over "Add to trip".
 */
const PlaceModalBody: React.FC<PlaceModalBodyProps> = ({
  locationData,
  name,
  address,
  description,
  phone,
  website,
  cityName,
  country,
  menuUrl,
  reservationUrl,
  openTableRid,
  photoUrls,
  onPhotosClick,
  explicitRecommendations,
  recommendationAvatars,
  tipsLocationId,
  tipsRefreshSignal,
  onReadMoreTips,
  onAddTip,
  isLoggedIn,
  onSignInClick,
  onAddToTripClick,
  totalPostsCount,
  associatedPosts,
  postsLoading,
  postsError,
  postsNeedSignIn,
  isCreatingTrip,
  isEnriching,
  onCreateTrip,
  onRetryPosts,
  mapPostToCard,
  isStayCategory,
  canBook,
  isBookLoading,
  onBookNow,
  showInlineStayBookingOptions = false,
  showStay22Vendors = false,
  isNuiteeBookLoading = false,
  isStay22BookLoading = false,
  onPlaceStayBookNuitee,
  onPlaceStayBookStay22Vendor,
  onGtExclusiveSignInClick,
  hotelWidgetImpression,
  canShowExperiences = false,
  experiencesSearchQuery = null,
  experiencesPostId,
  experiencesPlaceId,
  experiencesUserId,
  experiencesGooglePlaceId,
  onBrowseExperiences,
  isExperiencesLoading = false,
}) => {
  const isMobile = useIsMobileViewport();
  const bookingWidgetRef = useRef<PlaceModalBookingWidgetRef>(null);
  const bookingCardRef = useRef<HTMLDivElement>(null);
  const tipsRef = useRef<HTMLDivElement>(null);

  const showStayBooking = isStayCategory && canBook;
  const showExperiences = canShowExperiences && !!onBrowseExperiences && !!experiencesSearchQuery;

  useTrackHotelWidgetShown(showStayBooking, {
    surface: 'place_modal',
    cta_type: 'book_your_stay',
    ...hotelWidgetImpression,
  });

  const category = getPlaceCategoryDisplay({
    gt_category: locationData?.gt_category,
    category_name: locationData?.category_name,
    google_types: locationData?.google_types ?? locationData?.google_places_data?.types,
  });
  const cityLabel = getPlaceCityLabel(cityName, country, locationData, address);
  // OpenTable integration isn't picked yet: a reservation link, else OpenTable's page for the rid
  const tableBookingUrl =
    reservationUrl ||
    (openTableRid ? `https://www.opentable.com/restref/client/?rid=${encodeURIComponent(openTableRid)}` : undefined);
  const gpd = locationData?.google_places_data as Record<string, unknown> | undefined;
  const hours = useMemo(() => extractGoogleOpeningHoursDisplay(gpd), [gpd]);
  const googleMapsUrl = useMemo(() => pickPrimaryGoogleMapsUrl(locationData), [locationData]);
  const directionsUrl = useMemo(
    () => buildDirectionsUrl(locationData, name, address),
    [locationData, name, address],
  );

  const googleRating = getGoogleRating(locationData);
  const googleUserRatingCount = getGoogleUserRatingCountUnified(gpd);
  const scrollToTips = () => tipsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const city = getPlaceCity(cityName, locationData);

  const getBookingParams = () =>
    bookingWidgetRef.current?.getParams() ?? getDefaultPlaceModalBookingParams();

  const about = <PlaceModalAbout description={description} />;
  const infoRows = (
    <PlaceModalInfoRows
      address={address}
      directionsUrl={directionsUrl}
      hours={hours}
      website={website}
      menuUrl={menuUrl}
      tableBookingUrl={tableBookingUrl}
      phone={phone}
      phoneAside={
        isMobile && googleRating != null ? (
          <PlaceModalGoogleRating
            rating={googleRating}
            userRatingCount={googleUserRatingCount}
            mapsUrl={googleMapsUrl}
          />
        ) : undefined
      }
    />
  );
  const recommenders = (
    <PlaceModalRecommenders
      explicitRecommendations={explicitRecommendations}
      recommendationAvatars={recommendationAvatars}
      onReadTipsClick={showExperiences ? scrollToTips : undefined}
    />
  );

  return (
    <>
      <PlaceModalHero
        title={name}
        category={category}
        cityLabel={cityLabel}
        photoUrls={photoUrls}
        onPhotosClick={onPhotosClick}
      />

      <div className="flex flex-col gap-5 pb-3.5 pl-5 pr-5 pt-6 max-md:gap-8 max-md:pt-8">
        {showStayBooking ? (
          <div className="flex flex-col gap-4 md:flex-row md:items-start max-md:gap-8">
            <div className="flex min-w-0 flex-1 flex-col max-md:gap-6">
              {about}
              {infoRows}
            </div>
            <div ref={bookingCardRef} className="pt-2.5 md:w-[305px] md:shrink-0 max-md:pt-0">
              <div
                className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-[0px_6px_20px_rgba(0,0,0,0.15)] max-md:shadow-[0px_0px_20px_rgba(0,0,0,0.15)]"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className={clsx('m-0 font-manrope text-base font-semibold leading-[22px] tracking-[0.02em] text-[#252528]', MOBILE_SECTION_HEADING)}>
                  Book
                </h3>
                <PlaceModalBookingWidget ref={bookingWidgetRef} />
                {showInlineStayBookingOptions && onPlaceStayBookNuitee && onPlaceStayBookStay22Vendor ? (
                  <PlaceStayBookingOptions
                    showTitle={false}
                    showVendors={showStay22Vendors}
                    isLoggedIn={isLoggedIn}
                    isNuiteeLoading={isNuiteeBookLoading}
                    isStay22Loading={isStay22BookLoading}
                    getBookingParams={getBookingParams}
                    onSelectNuitee={onPlaceStayBookNuitee}
                    onSelectStay22Vendor={onPlaceStayBookStay22Vendor}
                    onSignInClick={onGtExclusiveSignInClick ?? onSignInClick}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => onBookNow({ ...getBookingParams(), button_action: 'book' })}
                    disabled={isBookLoading}
                    className={clsx(ORANGE_BUTTON, 'w-full')}
                  >
                    {isBookLoading ? 'Opening…' : 'Book'}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6 md:flex-row md:items-start">
            <div className="min-w-0 flex-1">{about}</div>
            <div className="min-w-0 flex-1">{infoRows}</div>
          </div>
        )}

        {isMobile ? (
          showExperiences && recommenders
        ) : (
          <PlaceModalSocialProof
            explicitRecommendations={explicitRecommendations}
            recommendationAvatars={recommendationAvatars}
            googleRating={googleRating}
            googleUserRatingCount={googleUserRatingCount}
            googleMapsUrl={googleMapsUrl}
            onReadTipsClick={scrollToTips}
          />
        )}

        {showExperiences && (
          <section
            className="flex flex-col items-center gap-4 bg-white px-2.5 pb-3.5 pt-1 max-md:p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-px w-full bg-[#E1E1E7] max-md:hidden" aria-hidden />
            <h3 className={clsx('m-0 w-full font-manrope text-base font-semibold leading-[22px] tracking-[0.02em] text-[#252528]', MOBILE_SECTION_HEADING)}>
              Tours &amp; experiences
            </h3>
            <div className="w-full">
              <GygActivityWidget
                surface="place_modal"
                query={experiencesSearchQuery}
                widgetKey="place"
                userId={experiencesUserId}
                postId={experiencesPostId}
                placeId={experiencesPlaceId}
                googlePlaceId={experiencesGooglePlaceId}
                locationName={name}
                density="compact"
              />
            </div>
            <p className="m-0 w-full font-manrope text-xs leading-4 tracking-[0.02em] text-[#525257]">
              Powered by GetYourGuide
            </p>
          </section>
        )}

        <div ref={tipsRef} className="scroll-mt-16">
          <PlaceModalCommunityTips
            locationId={tipsLocationId}
            isLoggedIn={isLoggedIn}
            refreshSignal={tipsRefreshSignal}
            onReadMore={onReadMoreTips}
            onAddTip={onAddTip}
            onSignInClick={onSignInClick}
            header={isMobile && !showExperiences ? recommenders : undefined}
          />
        </div>

        <PlaceModalFeaturedTrips
          totalPostsCount={totalPostsCount}
          associatedPosts={associatedPosts}
          postsLoading={postsLoading}
          postsError={postsError}
          needsSignIn={postsNeedSignIn}
          isCreatingTrip={isCreatingTrip}
          isEnriching={isEnriching}
          onCreateTrip={onCreateTrip}
          onSignInClick={onSignInClick}
          onRetryPosts={onRetryPosts}
          mapPostToCard={mapPostToCard}
        />
      </div>

      {/* Sticky actions — Figma bottom bar: add to trip + primary booking CTA (mobile: stacked, CTA on top) */}
      <div className="sticky bottom-0 z-10 mt-auto flex shrink-0 gap-3 border-t border-[#F0F0F0] bg-white px-4 py-3 max-md:flex-col-reverse max-md:gap-2 max-md:px-5 max-md:pb-[calc(12px+env(safe-area-inset-bottom,0px))]">
        <button
          type="button"
          onClick={onAddToTripClick}
          className={clsx(BAR_BUTTON, 'bg-[#252528] hover:bg-black')}
        >
          {isMobile && city ? `Add to My ${city} Trip` : 'Add to trip'}
        </button>
        {showExperiences && (
          <button
            type="button"
            onClick={onBrowseExperiences}
            disabled={isExperiencesLoading}
            className={BAR_PRIMARY}
          >
            {isExperiencesLoading ? 'Opening…' : 'See Tickets & Tours'}
          </button>
        )}
        {showStayBooking && (
          <button
            type="button"
            onClick={() => bookingCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            className={BAR_PRIMARY}
          >
            {isMobile ? 'Book Hotel' : 'Check availability'}
          </button>
        )}
        {isMobile && !showExperiences && !showStayBooking && tableBookingUrl && (
          <a href={tableBookingUrl} target="_blank" rel="noopener noreferrer" className={BAR_PRIMARY}>
            Make a Reservation
          </a>
        )}
      </div>
    </>
  );
};

export default PlaceModalBody;
